import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ProfilePage.css';
import { useStore } from '../store/useStore';
import { formatNumber } from '../utils/formatters';
import api, { gameAPI } from '../services/api';
import SoundManager from '../services/SoundManager';
import BoostCard from '../components/BoostCard';
import PnlSummaryCard from '../components/PnlSummaryCard';

import PnlModal from '../components/PnlModal';
import tonmoonLogo from '../assets/tonmoon-logo.png';
import usdtLogo from '../assets/usdt-logo.png';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  progress: number;
  maxProgress: number;
  reward: number;
}

// Новый интерфейс для достижений с кастомизацией луны
interface MoonAchievement {
  code: string;
  name: string;
  description: string;
  icon: string;
  conditionType: string;
  conditionValue: number;
  currentProgress: number;
  isCompleted: boolean;
  completedAt: string | null;
  rewardClaimed: boolean;
  rewardSperm: number;
  unlockColors: number[];
  unlockBackground: number | null;
  unlockEffect: number | null;
}

interface MoonColor {
  id: number;
  name: string;
  cssClass: string;
  hexColor: string | null;
  imagePath: string;
  isAnimated: boolean;
  rarity: string;
}

interface Background {
  id: number;
  name: string;
  fileName: string;
  imagePath: string;
}

interface MoonEffect {
  id: number;
  name: string;
  code: string;
  cssClass: string;
}

interface Customization {
  moonColor: number;
  backgroundId: number;
  moonEffects: number[];
  unlockedColors: number[];
  unlockedBackgrounds: number[];
  unlockedEffects: number[];
}

interface Catalogs {
  colors: MoonColor[];
  backgrounds: Background[];
  effects: MoonEffect[];
}

interface ProfileStats {
  daysInGame: number;
  totalEarned: number;
  totalEarnedUsdt: number;  // USDT заработок от марафона
  totalClaims: number;
  gamesPlayed: number;
  gamesWon: number;
  referralsInvited: number;
  totalDeposited: number;
  totalWithdrawn: number;
  dailyStreak: number;
  bestStreak: number;
}

interface ReferralLevelStats {
  level: number;
  referralCount: number;
  activeCount: number;
  isUnlocked: boolean;
}

interface ReferralsStats {
  totalStats: {
    totalReferrals: number;
    activeReferrals: number;
    availableLevel: number;
  };
  levelsStats: ReferralLevelStats[];
}

// Интерфейс для транзакции
interface Transaction {
  id: number;
  type: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  created_at: string;
  metadata: any;
  referral_info?: {
    display_name: string;
    level: number;
  };
}

// Интерфейс для данных кошелька
interface WalletData {
  sperm_balance: number;
  piggybank_balance: number;
  usdt_balance: number;
  piggybank_usdt: number;
  wallet_address: string | null;
}

// Интерфейс для данных пригласителя
interface ReferrerData {
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
}

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    user,
    updateBalance,
    graphicsQuality,
    language,
    soundEnabled,
    musicEnabled,
    selectedMusic,
    setGraphicsQuality,
    setLanguage,
    setSoundEnabled,
    setMusicEnabled,
    setSelectedMusic
  } = useStore();
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [showBoostsInfoModal, setShowBoostsInfoModal] = useState(false);
  const [showCouponsInfoModal, setShowCouponsInfoModal] = useState(false);
  // const [showSettingsModal, setShowSettingsModal] = useState(false); // Убрано
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [profileStats, setProfileStats] = useState<ProfileStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [walletConnected, setWalletConnected] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showLinkTelegram, setShowLinkTelegram] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  // Привязка Email (из ТГ)
  const [showLinkEmail, setShowLinkEmail] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [linkEmailCode, setLinkEmailCode] = useState('');
  const [linkEmailStep, setLinkEmailStep] = useState<'email' | 'code'>('email');
  const [linkEmailLoading, setLinkEmailLoading] = useState(false);
  const [linkEmailError, setLinkEmailError] = useState('');
  const [boosts, setBoosts] = useState<any>({
    vip: null,
    rank: null,
    leader: null,
    coupons: []
  });
  const [totalAprBonus, setTotalAprBonus] = useState(0.0);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [activatedCoupon, setActivatedCoupon] = useState<any>(null);

  // Состояния для кастомизации луны
  const [moonAchievements, setMoonAchievements] = useState<MoonAchievement[]>([]);
  const [customization, setCustomization] = useState<Customization | null>(null);
  const [catalogs, setCatalogs] = useState<Catalogs | null>(null);
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState<number>(1);
  const [selectedBackground, setSelectedBackground] = useState<number>(1);
  const [selectedEffects, setSelectedEffects] = useState<number[]>([]);
  const [savingCustomization, setSavingCustomization] = useState(false);

  // Состояние для статистики рефералов по линиям
  const [referralsStats, setReferralsStats] = useState<ReferralsStats | null>(null);

  // Состояния для кошелька и истории операций
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState<'week' | 'month' | 'all'>('all');
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hideSlots, setHideSlots] = useState(false);
  const [hideReferralDaily, setHideReferralDaily] = useState(false);
  const [showPnlModal, setShowPnlModal] = useState(false);
  const [referrerData, setReferrerData] = useState<ReferrerData | null>(null);
  const HISTORY_LIMIT = 20;

  // Хелпер для форматирования APR бонуса с правильной точностью
  const formatAprBonus = (value: number): string => {
    if (value === 0) return '0';
    if (value >= 0.01) return value.toFixed(2);
    if (value >= 0.001) return value.toFixed(3);
    return value.toFixed(4);
  };

  // Загружаем данные профиля
  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Загружаем статистику профиля, достижения, бусты, купоны, кастомизацию, рефералов и пригласителя независимо
      const [statsRes, achievementsRes, boostsRes, couponsRes, customizationRes, referralsRes, referrerRes] = await Promise.allSettled([
        api.get('/user/stats'),
        api.get(`/achievements?lang=${i18n.language}`),
        api.get('/user/boosts'),
        api.get('/coupons'),
        api.get(`/achievements/customization-page?lang=${i18n.language}`),
        api.get('/referrals/my'),
        api.get('/user/referrer')
      ]);

      // Обрабатываем stats и маппим поля
      if (statsRes.status === 'fulfilled' && statsRes.value.data.success) {
        const data = statsRes.value.data.data;
        setProfileStats({
          daysInGame: data.activity?.days_active || 0,
          totalEarned: data.balance?.earned || 0,
          totalEarnedUsdt: data.balance?.earned_usdt || 0,  // USDT от марафона
          totalClaims: data.activity?.total_claims || 0,
          gamesPlayed: data.games?.total_played || 0,
          gamesWon: data.games?.games_won || 0,
          referralsInvited: data.referrals?.count || 0,
          totalDeposited: data.balance?.deposited || 0,
          totalWithdrawn: data.balance?.withdrawn || 0,
          dailyStreak: data.activity?.claim_streak || 0,
          bestStreak: data.activity?.claim_streak || 0
        });
      }

      // Обрабатываем achievements
      if (achievementsRes.status === 'fulfilled' && achievementsRes.value.data.success) {
        setAchievements(achievementsRes.value.data.data);
      }

      // Обрабатываем boosts
      if (boostsRes.status === 'fulfilled' && boostsRes.value.data.success) {
        setBoosts(boostsRes.value.data.boosts || {
          vip: null,
          rank: null,
          leader: null,
          coupons: []
        });
        setTotalAprBonus(parseFloat(boostsRes.value.data.totalAprBonus) || 0.0);
      }

      // Обрабатываем coupons
      if (couponsRes.status === 'fulfilled' && couponsRes.value.data.success) {
        setCoupons(couponsRes.value.data.coupons || []);
      }

      // Обрабатываем данные кастомизации луны
      if (customizationRes.status === 'fulfilled' && customizationRes.value.data.success) {
        const customData = customizationRes.value.data.data;
        setMoonAchievements(customData.achievements || []);
        setCustomization(customData.customization || null);
        setCatalogs(customData.catalogs || null);

        // Устанавливаем текущие выбранные значения
        if (customData.customization) {
          setSelectedColor(customData.customization.moonColor || 1);
          setSelectedBackground(customData.customization.backgroundId || 1);
          setSelectedEffects(customData.customization.moonEffects || []);
        }
      }

      // Обрабатываем данные рефералов по линиям
      if (referralsRes.status === 'fulfilled' && referralsRes.value.data.success) {
        const refData = referralsRes.value.data.data;
        setReferralsStats({
          totalStats: refData.totalStats,
          levelsStats: refData.levelsStats
        });
      }

      // Обрабатываем данные пригласителя
      if (referrerRes.status === 'fulfilled' && referrerRes.value.data.success) {
        setReferrerData(referrerRes.value.data.data);
      }

      // Проверяем email юзера
      try {
        const verifyRes = await api.get('/auth/verify');
        if (verifyRes.data.success && verifyRes.data.user?.email) {
          setUserEmail(verifyRes.data.user.email);
        }
      } catch (e) { /* тихо */ }

      // Загружаем данные кошелька отдельно
      try {
        const [balanceRes, claimStatusRes] = await Promise.all([
          api.get('/wallet/balance'),
          api.get('/user/claim-status')
        ]);

        if (balanceRes.data.success && claimStatusRes.data.success) {
          const balance = balanceRes.data.data;
          const claimData = claimStatusRes.data.data;
          setWalletData({
            sperm_balance: balance.sperm_balance || 0,
            piggybank_balance: claimData.piggyBankBalance || 0,
            usdt_balance: claimData.balanceUsdt || 0,
            piggybank_usdt: claimData.piggyBankUsdt || 0,
            wallet_address: balance.wallet_address || null
          });
        }
      } catch (walletError) {
        console.error('Error loading wallet data:', walletError);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка истории операций
  const loadTransactions = async (period: 'week' | 'month' | 'all' = historyPeriod, page: number = 0, hideSlotsParam: boolean = hideSlots, hideRefDailyParam: boolean = hideReferralDaily) => {
    try {
      setLoadingHistory(true);
      const offset = page * HISTORY_LIMIT;
      const response = await api.get(`/wallet/transactions?limit=${HISTORY_LIMIT}&offset=${offset}&period=${period}&hideSlots=${hideSlotsParam}&hideReferralDaily=${hideRefDailyParam}`);

      if (response.data.success) {
        setTransactions(response.data.data.transactions);
        setHistoryTotal(response.data.data.total);
        setHistoryPage(page);
        setHistoryPeriod(period);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Получить иконку и название типа транзакции
  const getTransactionInfo = (type: string): { icon: string; label: string } => {
    const types: Record<string, { icon: string; label: string }> = {
      deposit: { icon: '📥', label: t('profile.txDeposit') },
      withdrawal: { icon: '📤', label: t('profile.txWithdrawal') },
      withdrawal_usdt: { icon: '📤', label: t('profile.txWithdrawalUsdt') },
      lunar_staking: { icon: '🌙', label: t('profile.txLunarStaking') },
      lunar_staking_ton: { icon: '🌙', label: t('profile.txLunarStakingTon') },
      lunar_staking_ton_failed: { icon: '🌙', label: t('profile.txLunarStakingTonFailed') },
      star_staking: { icon: '⭐', label: t('profile.txStarStaking') },
      star_staking_ton: { icon: '⭐', label: t('profile.txStarStakingTon') },
      star_withdrawal: { icon: '⭐', label: t('profile.txStarWithdrawal') },
      staking_reward: { icon: '💰', label: t('profile.txStakingReward') },
      referral_staking: { icon: '🎁', label: t('profile.txReferralStaking') },
      referral_daily: { icon: '💸', label: t('profile.txReferralDaily') },
      referral_daily_usdt: { icon: '💵', label: t('profile.txReferralDailyUsdt') },
      referral_marathon_bonus: { icon: '🎁', label: t('profile.txReferralMarathonBonus') },
      slots: { icon: '🎰', label: t('profile.txSlots') },
      slots_bet: { icon: '🎰', label: t('profile.txSlotsBet') },
      slots_win: { icon: '🎰', label: t('profile.txSlotsWin') },
      game_play: { icon: '🎰', label: t('profile.txGamePlay') },
      minigame: { icon: '🎮', label: t('profile.txMinigame') },
      piggybank_deposit: { icon: '🐷', label: t('profile.txPiggybankDeposit') },
      piggybank_withdrawal: { icon: '🐷', label: t('profile.txPiggybankWithdrawal') },
      piggybank_usdt_withdraw: { icon: '🐷', label: t('profile.txPiggybankUsdtWithdraw') },
      piggybank_interest: { icon: '🐷', label: t('profile.txPiggybankInterest') },
      piggybank_transfer_to_slots: { icon: '🎰', label: t('profile.txPiggybankTransferToSlots') },
      marathon_deposit_usdt: { icon: '🏁', label: t('profile.txMarathonDepositUsdt') },
      marathon_deposit: { icon: '🏁', label: t('profile.txMarathonDeposit') },
      marathon_deposit_ton: { icon: '🏁', label: t('profile.txMarathonDepositTon') },
      marathon_profit: { icon: '🏁', label: t('profile.txMarathonProfit') },
      marathon_profit_claim: { icon: '🏁', label: t('profile.txMarathonProfitClaim') },
      marathon_usdt_piggybank: { icon: '🏁', label: t('profile.txMarathonUsdtPiggybank') },
      marathon_usdt_balance: { icon: '🏁', label: t('profile.txMarathonUsdtBalance') },
      marathon_burn: { icon: '🔥', label: t('profile.txMarathonBurn') },
      marathon_contribution: { icon: '🏁', label: t('profile.txMarathonContribution') },
      marathon_unfreeze: { icon: '🏁', label: t('profile.txMarathonUnfreeze') },
      deposit_ton_refund: { icon: '↩️', label: t('profile.txDepositTonRefund') },
      admin_deposit: { icon: '👑', label: t('profile.txAdminDeposit') },
      admin_withdrawal: { icon: '👑', label: t('profile.txAdminWithdrawal') },
      task_reward: { icon: '📋', label: t('profile.txTaskReward') },
      adsgram_reward: { icon: '📺', label: t('profile.txAdsgramReward') }
    };
    return types[type] || { icon: '💫', label: t('profile.txUnknown') };
  };

  // Форматирование даты
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Копирование адреса кошелька
  const copyWalletAddress = () => {
    if (walletData?.wallet_address) {
      navigator.clipboard.writeText(walletData.wallet_address);
      SoundManager.playButtonClick();
      // Можно добавить toast уведомление
    }
  };

  // Сохранение кастомизации
  const saveCustomization = async () => {
    try {
      setSavingCustomization(true);
      const response = await api.post('/achievements/customization', {
        moonColor: selectedColor,
        backgroundId: selectedBackground,
        moonEffects: selectedEffects
      });

      if (response.data.success) {
        setCustomization(response.data.data);
        SoundManager.playClaimSuccess();
        setShowCustomizationModal(false);
      }
    } catch (error: any) {
      console.error('Error saving customization:', error);
      alert(error.response?.data?.message || t('profile.errorSaving'));
    } finally {
      setSavingCustomization(false);
    }
  };

  // Переключение эффекта
  const toggleEffect = (effectId: number) => {
    if (selectedEffects.includes(effectId)) {
      setSelectedEffects(selectedEffects.filter(id => id !== effectId));
    } else {
      setSelectedEffects([...selectedEffects, effectId]);
    }
  };

  // Все данные (ранги, VIP, статусы) приходят из API /user/boosts - моковые данные удалены
  const daysInGame = profileStats?.daysInGame || 0;

  // Сохранение настроек - убрано
  // const handleSaveSettings = () => {
  //   localStorage.setItem('graphicsQuality', graphicsQuality);
  //   localStorage.setItem('language', language);
  //   localStorage.setItem('soundEnabled', soundEnabled ? 'true' : 'false');
  //   // setShowSettingsModal(false);
  //   // Применяем настройки
  //   // document.querySelector('.app')?.setAttribute('data-quality', graphicsQuality);
  //   // document.querySelector('.app')?.setAttribute('data-lang', language);
  // };

  return (
    <div className="profile-page">
      <div className="page-content">
        {/* Информация о пользователе */}
        <div className="user-info-card">
          <div className="user-avatar" onClick={() => { if (localStorage.getItem('webauth') === 'true') setShowAvatarPicker(true); }} style={{ cursor: localStorage.getItem('webauth') === 'true' ? 'pointer' : 'default', position: 'relative' }}>
            {(() => {
              const isWeb = localStorage.getItem('webauth') === 'true';
              const savedAvatar = localStorage.getItem('user_avatar');
              if (savedAvatar) {
                return <>
                  <img src={savedAvatar} alt="" className="avatar-image" />
                  {isWeb && <span style={{position:'absolute',bottom:0,right:0,background:'rgba(240,185,11,0.9)',borderRadius:'50%',width:'22px',height:'22px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px'}}>✏️</span>}
                </>;
              }
              if (user?.telegramId && !String(user.telegramId).startsWith('-')) {
                return (
                  <img
                    src={`/api/user/photo/${user.telegramId}?v=${Date.now()}`}
                    alt=""
                    className="avatar-image"
                    crossOrigin="anonymous"
                    loading="eager"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLImageElement).parentElement;
                      if (parent && !parent.querySelector('.avatar-icon')) {
                        const fallback = document.createElement('span');
                        fallback.className = 'avatar-icon';
                        fallback.textContent = '👤';
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                );
              }
              return <span className="avatar-icon">👤</span>;
            })()}
            {localStorage.getItem('webauth') === 'true' && (
              <span style={{position:'absolute',bottom:0,right:0,background:'rgba(240,185,11,0.9)',borderRadius:'50%',width:'22px',height:'22px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',boxShadow:'0 2px 6px rgba(0,0,0,0.3)'}}>✏️</span>
            )}
          </div>
          <div className="user-details">
            {(() => {
              const webUser = localStorage.getItem('webauth') === 'true' ? (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })() : null;
              const displayName = user?.firstName || user?.username || webUser?.firstName || webUser?.username || t('profile.player');
              const displayId = user?.id || webUser?.id || '---';
              const displayUsername = user?.username || webUser?.username;
              const tgId = String(user?.telegramId || webUser?.telegramId || '');
              return <>
                <h2 className="user-name">
                  {displayName}
                  {(user?.lastName || webUser?.lastName) && ` ${user?.lastName || webUser?.lastName}`}
                </h2>
                <p className="user-id">ID: {displayId}</p>
                {displayUsername && <p className="user-username">@{displayUsername}</p>}
              </>;
            })()}
            {(() => {
              const webUser = localStorage.getItem('webauth') === 'true' ? (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })() : null;
              const tgId = String(user?.telegramId || webUser?.telegramId || '');
              return localStorage.getItem('webauth') === 'true' && tgId.startsWith('-');
            })() && (
              <button className="link-tg-btn" onClick={() => setShowLinkTelegram(true)} style={{
                margin: '6px 0', padding: '6px 14px', background: 'rgba(0,136,204,0.15)',
                border: '1px solid rgba(0,136,204,0.3)', borderRadius: '8px', color: '#0088cc',
                fontSize: '12px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <span>🔗</span> {t('profile.linkTelegram') || 'Привязать Telegram'}
              </button>
            )}
            {/* Привязать Email — для ТГ юзеров без email */}
            {localStorage.getItem('webauth') !== 'true' && !userEmail && (
              <button className="link-tg-btn" onClick={() => { setShowLinkEmail(true); setLinkEmailStep('email'); setLinkEmailError(''); setLinkEmailInput(''); setLinkEmailCode(''); }} style={{
                margin: '6px 0', padding: '6px 14px', background: 'rgba(251,191,36,0.12)',
                border: '1px solid rgba(251,191,36,0.3)', borderRadius: '8px', color: '#fbbf24',
                fontSize: '12px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}>
                <span>📧</span> {t('profile.linkEmail') || 'Привязать Email'}
              </button>
            )}
            {/* Email уже привязан — с кнопкой сменить */}
            {userEmail && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 0' }}>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                  📧 {userEmail}
                </p>
                <button onClick={() => { setShowLinkEmail(true); setLinkEmailStep('email'); setLinkEmailError(''); setLinkEmailInput(''); setLinkEmailCode(''); }} style={{
                  background: 'none', border: 'none', color: '#fbbf24', fontSize: '11px', cursor: 'pointer', padding: '2px 6px',
                  opacity: 0.7, textDecoration: 'underline'
                }}>
                  {t('profile.changeEmail') || 'сменить'}
                </button>
              </div>
            )}
            {referrerData && (
              <p className="user-referrer">
                <span className="referrer-label">{t('profile.invitedBy') || 'Пригласил:'}</span>
                {referrerData.username ? (
                  <a
                    href={`https://t.me/${referrerData.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="referrer-link"
                  >
                    @{referrerData.username}
                  </a>
                ) : (
                  <span className="referrer-name">{referrerData.displayName}</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Moon Fuel убран */}

        {/* PNL карточка */}
        <PnlSummaryCard onOpenModal={() => setShowPnlModal(true)} />

        {/* Карточка кошелька */}
        <div className="wallet-card">
          <div className="card-header-with-info">
            <h3 className="card-title">💼 {t('profile.myWallet') || 'Мой кошелек'}</h3>
          </div>

          {walletData && (
            <div className="wallet-balances">
              <div className="wallet-balance-row">
                <img src={tonmoonLogo} alt="SPERM" className="balance-coin-icon" />
                <span className="balance-label">SPERM</span>
                <span className="balance-value">{formatNumber(walletData.sperm_balance, 2)}</span>
              </div>
              <div className="wallet-balance-row piggy">
                <span className="balance-icon">🐷</span>
                <span className="balance-label">{t('profile.piggybank') || 'Копилка'}</span>
                <span className="balance-value">{formatNumber(walletData.piggybank_balance, 2)} SPERM</span>
              </div>
              <div className="wallet-balance-row usdt">
                <img src={usdtLogo} alt="USDT" className="balance-coin-icon" />
                <span className="balance-label">USDT</span>
                <span className="balance-value">{formatNumber(walletData.usdt_balance, 2)}</span>
              </div>
              {walletData.piggybank_usdt > 0 && (
                <div className="wallet-balance-row piggy-usdt">
                  <span className="balance-icon">🐷</span>
                  <span className="balance-label">{t('profile.piggybank') || 'Копилка'} USDT</span>
                  <span className="balance-value">{formatNumber(walletData.piggybank_usdt, 2)}</span>
                </div>
              )}
            </div>
          )}

          {walletData?.wallet_address && (
            <div className="wallet-address-row" onClick={copyWalletAddress}>
              <span className="address-icon">📜</span>
              <span className="address-label">{t('profile.address') || 'Адрес'}:</span>
              <span className="address-value">
                {walletData.wallet_address.slice(0, 6)}...{walletData.wallet_address.slice(-4)}
              </span>
              <span className="copy-icon">📋</span>
            </div>
          )}

          <button
            className="history-toggle-btn"
            onClick={() => {
              SoundManager.playButtonClick();
              if (!showHistory) {
                loadTransactions();
              }
              setShowHistory(!showHistory);
            }}
          >
            📊 {t('profile.transactionHistory') || 'История операций'} {showHistory ? '▲' : '▼'}
          </button>

          {/* История операций */}
          {showHistory && (
            <div className="history-section">
              {/* Фильтры по периоду */}
              <div className="history-filters">
                <button
                  className={`filter-btn ${historyPeriod === 'week' ? 'active' : ''}`}
                  onClick={() => loadTransactions('week', 0)}
                >
                  {t('profile.week') || 'Неделя'}
                </button>
                <button
                  className={`filter-btn ${historyPeriod === 'month' ? 'active' : ''}`}
                  onClick={() => loadTransactions('month', 0)}
                >
                  {t('profile.month') || 'Месяц'}
                </button>
                <button
                  className={`filter-btn ${historyPeriod === 'all' ? 'active' : ''}`}
                  onClick={() => loadTransactions('all', 0)}
                >
                  {t('profile.all') || 'Все'}
                </button>
                <span className="history-count">
                  {historyTotal} {t('profile.records') || 'записей'}
                </span>
                <button
                  className={`filter-btn hide-slots-btn ${hideSlots ? 'active' : ''}`}
                  onClick={() => {
                    const newHideSlots = !hideSlots;
                    setHideSlots(newHideSlots);
                    loadTransactions(historyPeriod, 0, newHideSlots, hideReferralDaily);
                  }}
                >
                  {hideSlots ? `🎰 ${t('profile.showSlots')}` : `🎰 ${t('profile.hideSlots')}`}
                </button>
                <button
                  className={`filter-btn hide-referral-btn ${hideReferralDaily ? 'active' : ''}`}
                  onClick={() => {
                    const newHideRefDaily = !hideReferralDaily;
                    setHideReferralDaily(newHideRefDaily);
                    loadTransactions(historyPeriod, 0, hideSlots, newHideRefDaily);
                  }}
                >
                  {hideReferralDaily ? `💸 ${t('profile.showReferralDaily')}` : `💸 ${t('profile.hideReferralDaily')}`}
                </button>
              </div>

              {/* Список транзакций */}
              <div className="transactions-list">
                {loadingHistory ? (
                  <div className="loading-history">{t('common.loading') || 'Загрузка...'}</div>
                ) : transactions.length > 0 ? (
                  transactions.map((tx) => {
                    const info = getTransactionInfo(tx.type);
                    const isPositive = tx.amount > 0 && !['withdrawal', 'withdrawal_usdt', 'lunar_staking', 'star_staking', 'game_play', 'piggybank_deposit', 'marathon_deposit_usdt'].includes(tx.type);
                    // Формируем метку с информацией о реферале
                    let label = info.label;
                    const isReferralType = ['referral_staking', 'referral_daily', 'referral_daily_usdt', 'referral_marathon_bonus'].includes(tx.type);

                    if (tx.referral_info) {
                      // Новые транзакции с metadata - показываем точно от кого
                      label = `${info.label} • ${tx.referral_info.display_name} (Lv.${tx.referral_info.level})`;
                    } else if (isReferralType && tx.description) {
                      // Старые транзакции - парсим description
                      if (tx.type === 'referral_marathon_bonus') {
                        // "Единовременный бонус X% от марафона реферала USERNAME"
                        const match = tx.description.match(/реферала\s+(\S+)/);
                        if (match) {
                          label = `${info.label} • @${match[1]}`;
                        }
                      } else if (tx.type === 'referral_daily' || tx.type === 'referral_daily_usdt') {
                        // "Ежедневное вознаграждение от X рефералов"
                        const match = tx.description.match(/от\s+(\d+)\s+реферал/);
                        if (match) {
                          label = `${info.label} • (${match[1]} ${t('profile.peopleAbbr')})`;
                        }
                      } else if (tx.type === 'referral_staking') {
                        // Старый формат - просто показываем описание кратко
                        const match = tx.description.match(/реферала\s+(\S+)/);
                        if (match) {
                          label = `${info.label} • @${match[1]}`;
                        }
                      }
                    }

                    // Специальное отображение для слотов - показываем ставку и выигрыш
                    const isSlots = tx.type === 'slots' || tx.type === 'slots_daily' || tx.type === 'game_play';
                    const slotsBet = tx.metadata?.bet;
                    const slotsWin = tx.metadata?.win;
                    const hasSlotDetails = isSlots && slotsBet !== undefined && slotsWin !== undefined;

                    // Отображение статуса для выводов
                    const isWithdrawal = tx.type === 'withdrawal' || tx.type === 'withdrawal_usdt';
                    let withdrawalStatusLabel = '';
                    if (isWithdrawal && tx.status) {
                      if (tx.status === 'pending') {
                        withdrawalStatusLabel = t('profile.txWithdrawalPending');
                      } else if (tx.status === 'processing') {
                        withdrawalStatusLabel = t('profile.txWithdrawalProcessing');
                      } else if (tx.status === 'completed') {
                        withdrawalStatusLabel = t('profile.txWithdrawalCompleted');
                      }
                    }

                    return (
                      <div key={tx.id} className="transaction-item">
                        <div className="tx-left">
                          <span className="tx-icon">{info.icon}</span>
                          <div className="tx-info">
                            <span className="tx-label">
                              {label}
                              {withdrawalStatusLabel && <span className="tx-status-badge">{withdrawalStatusLabel}</span>}
                            </span>
                            <span className="tx-date">{formatDate(tx.created_at)}</span>
                          </div>
                        </div>
                        <div className="tx-right">
                          {hasSlotDetails ? (
                            <div className="slots-amounts">
                              <span className="tx-amount negative">−{formatNumber(slotsBet, 2)}</span>
                              <span className="slots-separator">/</span>
                              <span className={`tx-amount ${slotsWin > 0 ? 'positive' : 'zero'}`}>
                                +{formatNumber(slotsWin, 2)}
                              </span>
                            </div>
                          ) : (
                            <span className={`tx-amount ${isPositive ? 'positive' : 'negative'}`}>
                              {isPositive ? '+' : ''}{formatNumber(tx.amount, 2)} {tx.currency}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-transactions">{t('profile.noTransactions') || 'Нет операций'}</div>
                )}
              </div>

              {/* Пагинация */}
              {historyTotal > HISTORY_LIMIT && (
                <div className="history-pagination">
                  <button
                    className="pagination-btn"
                    disabled={historyPage === 0}
                    onClick={() => loadTransactions(historyPeriod, historyPage - 1)}
                  >
                    ◀ {t('common.prev') || 'Назад'}
                  </button>
                  <span className="pagination-info">
                    {historyPage + 1} / {Math.ceil(historyTotal / HISTORY_LIMIT)}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={(historyPage + 1) * HISTORY_LIMIT >= historyTotal}
                    onClick={() => loadTransactions(historyPeriod, historyPage + 1)}
                  >
                    {t('common.next') || 'Вперед'} ▶
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Карточка бустов - новый дизайн */}
        <div className="boosts-card-new">
          <div className="card-header-with-info">
            <h3 className="card-title">{t('profile.myBoosts')}</h3>
            <button
              className="header-help-button"
              onClick={() => {
                SoundManager.playModalOpen();
                setShowBoostsInfoModal(true);
              }}
              title={t('profile.boostsInfoButton')}
            >
              <span className="help-icon">?</span>
            </button>
          </div>

          {/* Карточка Total APR Bonus - показывается ВСЕГДА */}
          <div className="total-apr-display">
            <div className="apr-text-block">
              <span className="apr-label">{t('profile.totalAprBonus')}</span>
              <span className="apr-hint">{t('profile.totalAprBonusHint')}</span>
              {/* Детализация бонусов */}
              <div className="apr-breakdown">
                {boosts.vip?.current?.unlocked && boosts.vip.current.aprBonus > 0 && (
                  <div className="apr-breakdown-item">
                    💎 {t(`vipLevels.${boosts.vip.current.level}`)}: +{formatAprBonus(boosts.vip.current.aprBonus)}%
                  </div>
                )}
                {boosts.rank?.current?.unlocked && boosts.rank.current.aprBonus > 0 && (
                  <div className="apr-breakdown-item">
                    🎖️ {t(`ranks.${boosts.rank.current.level}`)}: +{formatAprBonus(boosts.rank.current.aprBonus)}%
                  </div>
                )}
                {boosts.leader?.current?.unlocked && boosts.leader.current.aprBonus > 0 && (
                  <div className="apr-breakdown-item">
                    👑 {t(`leaders.${boosts.leader.current.level}`)}: +{formatAprBonus(boosts.leader.current.aprBonus)}%
                  </div>
                )}
              </div>
            </div>
            <span className="apr-value">+{formatAprBonus(totalAprBonus)}%</span>
          </div>

          <div className="boosts-list">
            {/* VIP статус */}
            {boosts.vip && (
              <BoostCard boostStatus={boosts.vip} category="vip" />
            )}

            {/* Ранг */}
            {boosts.rank && (
              <BoostCard boostStatus={boosts.rank} category="rank" />
            )}

            {/* Лидерский статус */}
            {boosts.leader && (
              <BoostCard boostStatus={boosts.leader} category="leader" />
            )}
          </div>

          {/* Если нет данных */}
          {!boosts.vip && !boosts.rank && !boosts.leader && (
            <div className="no-boosts">
              <p>{t('profile.loadingBoosts')}</p>
              <p className="hint">{t('profile.noBoostsHint')}</p>
            </div>
          )}
        </div>

        {/* Карточка купонов */}
        <div className="coupons-card">
          <div className="card-header-with-info">
            <h3 className="card-title">{t('profile.myCoupons')}</h3>
            <button
              className="header-help-button"
              onClick={() => {
                SoundManager.playModalOpen();
                setShowCouponsInfoModal(true);
              }}
              title={t('profile.couponsInfoButton')}
            >
              <span className="help-icon">?</span>
            </button>
          </div>
          <div className="coupons-list">
{coupons.filter(c => !c.used).length > 0 ? (
              coupons.filter(c => !c.used).map((coupon) => {
                // Рассчитываем время в минутах или секундах
                const duration = coupon.duration_seconds >= 60
                  ? `${Math.floor(coupon.duration_seconds / 60)} ${t('profile.couponMinutes')}`
                  : `${coupon.duration_seconds} ${t('profile.couponSeconds')}`;

                // Рассчитываем процент буста
                const boostPercentage = Math.round((coupon.boost_multiplier - 1) * 100);

                return (
                  <div key={coupon.id} className="coupon-item">
                    <div className="coupon-content">
                      <div className="coupon-icon">⚡</div>
                      <div className="coupon-rank-badge">
                        {coupon.metadata?.source === 'achievement'
                          ? `🏆 ${coupon.metadata?.achievementName || t(`achievementsList.${coupon.metadata?.achievementCode}.name`, { defaultValue: coupon.metadata?.achievementName })}`
                          : `${t('profile.couponRank')} ${t(`ranks.${coupon.rank_level}`)}`
                        }
                      </div>

                      <div className="coupon-details">
                        <div className="coupon-boost">
                          <span className="coupon-boost-label">{t('profile.couponBoost')}</span>
                          <span className="coupon-boost-value">+{boostPercentage}%</span>
                        </div>
                        <div className="coupon-duration">
                          <span className="coupon-duration-label">{t('profile.couponDuration')}</span>
                          <span className="coupon-duration-value">{duration}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      className="coupon-activate-btn"
                      onClick={async () => {
                        try {
                          // Добавляем тактильную обратную связь (вибрацию)
                          const telegram = (window as any).Telegram;
                          if (telegram?.WebApp?.HapticFeedback) {
                            telegram.WebApp.HapticFeedback.impactOccurred('medium');
                          }

                          const response = await api.post(`/coupons/activate/${coupon.id}`);

                          // Успешная активация - звук и вибрация
                          SoundManager.playClaimSuccess();
                          if (telegram?.WebApp?.HapticFeedback) {
                            telegram.WebApp.HapticFeedback.notificationOccurred('success');
                          }

                          // ✅ КРИТИЧНО: Отправляем событие boostActivated на HomePage
                          // чтобы обновился mining_rate в реальном времени
                          if (response.data.success) {
                            const boostEvent = new CustomEvent('boostActivated', {
                              detail: {
                                multiplier: response.data.boost_multiplier || coupon.boost_multiplier,
                                duration: response.data.duration_seconds || coupon.duration_seconds,
                                expiresAt: response.data.expires_at,
                                source: 'rank_coupon'
                              }
                            });
                            window.dispatchEvent(boostEvent);
                            console.log('📢 [ProfilePage] Событие boostActivated отправлено:', boostEvent.detail);
                          }

                          // Показываем модальное окно с информацией об активированном купоне
                          setActivatedCoupon({
                            rank_level: coupon.rank_level,
                            boost_percentage: boostPercentage,
                            duration: duration
                          });
                          setShowCouponModal(true);

                          // Перезагружаем данные
                          await loadProfileData();
                        } catch (error: any) {
                          // Ошибка - вибрация ошибки
                          const telegram = (window as any).Telegram;
                          if (telegram?.WebApp?.HapticFeedback) {
                            telegram.WebApp.HapticFeedback.notificationOccurred('error');
                          }
                          alert(error.response?.data?.message || t('profile.errorActivation'));
                        }
                      }}
                    >
                      {t('profile.activate')}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="no-coupons">
                <p>{t('profile.noCouponsMessage')}</p>
                <p className="hint">{t('profile.noCouponsHint')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Достижения и кастомизация луны */}
        <div className="moon-section">
          <div className="moon-section-header">
            <h3>{t('profile.achievements')}</h3>
            <button
              className="customize-moon-btn"
              onClick={() => {
                SoundManager.playModalOpen();
                setShowCustomizationModal(true);
              }}
            >
              <span>🎨</span>
              {t('profile.customizeMoon')}
            </button>
          </div>

          {/* Превью текущей луны (без фона - фон только на главной) */}
          {customization && catalogs && (
            <div className="current-moon-preview">
              <div className={`moon-display ${customization.moonEffects.map(id => {
                const eff = catalogs.effects.find(e => e.id === id);
                return eff ? `effect-${eff.code}` : '';
              }).join(' ')}`}>
                <img
                  src={catalogs.colors.find(c => c.id === customization.moonColor)?.imagePath || '/assets/moons/moon-1.png'}
                  alt="Your Moon"
                  className="current-moon-img"
                />
              </div>
              <div className="moon-stats">
                <span className="stat-item">🔓 {customization.unlockedColors.length}/{catalogs.colors.length} {t('profile.colors')}</span>
              </div>
            </div>
          )}

          {/* Сетка достижений */}
          <div className="achievements-list">
            {moonAchievements.length > 0 ? (
              moonAchievements.map((achievement) => {
                const progress = Math.min(100, (achievement.currentProgress / achievement.conditionValue) * 100);
                return (
                  <div
                    key={achievement.code}
                    className={`achievement-card ${achievement.isCompleted ? 'completed' : ''}`}
                  >
                    <div className="achievement-left">
                      <span className="achievement-icon-big">{achievement.icon}</span>
                    </div>
                    <div className="achievement-center">
                      <span className="achievement-title">{t(`achievementsList.${achievement.code}.name`, { defaultValue: achievement.name })}</span>
                      <span className="achievement-desc">{t(`achievementsList.${achievement.code}.desc`, { defaultValue: achievement.description })}</span>
                      {!achievement.isCompleted && (
                        <div className="achievement-progress-bar">
                          <div className="progress-track">
                            <div className="progress-fill" style={{ width: `${progress}%` }} />
                          </div>
                          <span className="progress-label">{achievement.currentProgress}/{achievement.conditionValue}</span>
                        </div>
                      )}
                    </div>
                    <div className="achievement-right">
                      {achievement.isCompleted ? (
                        <span className="check-mark">✓</span>
                      ) : (
                        <span className="lock-icon">🔒</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              // Заглушки
              [
                { id: '1', icon: '🎯', name: t('profile.achievementFirst'), desc: t('profile.firstClaim') },
                { id: '2', icon: '📅', name: t('profile.achievementActive3'), desc: t('profile.active3days') },
                { id: '3', icon: '💎', name: t('profile.achievementInvestor'), desc: t('profile.firstStaking') },
                { id: '4', icon: '👥', name: t('profile.achievementMentor'), desc: t('profile.firstReferral') },
                { id: '5', icon: '🏆', name: t('profile.achievementVeteran'), desc: t('profile.veteran') },
                { id: '6', icon: '👑', name: t('profile.achievementVipElite'), desc: t('profile.vipEliteDesc') }
              ].map(achievement => (
                <div key={achievement.id} className="achievement-card">
                  <div className="achievement-left">
                    <span className="achievement-icon-big">{achievement.icon}</span>
                  </div>
                  <div className="achievement-center">
                    <span className="achievement-title">{achievement.name}</span>
                    <span className="achievement-desc">{achievement.desc}</span>
                  </div>
                  <div className="achievement-right">
                    <span className="lock-icon">🔒</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Статистика */}
        <div className="stats-card">
          <h3 className="card-title">{t('profile.generalStats')}</h3>
          <div className="stats-list">
            <div className="stat-row">
              <span className="stat-label">{t('profile.daysInGame')}</span>
              <span className="stat-value">{profileStats?.daysInGame || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t('profile.totalEarned')}</span>
              <span className="stat-value">{formatNumber(profileStats?.totalEarned || 0, 0)} SPERM</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t('profile.totalEarnedUsdt', 'Заработано USDT')}</span>
              <span className="stat-value">{formatNumber(profileStats?.totalEarnedUsdt || 0, 2)} USDT</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t('profile.claimsColl')}</span>
              <span className="stat-value">{profileStats?.totalClaims || user?.totalClaims || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t('profile.gamesPlayed')}</span>
              <span className="stat-value">{profileStats?.gamesPlayed || 0}</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">{t('profile.gamesWon')}</span>
              <span className="stat-value">{profileStats?.gamesWon || 0}</span>
            </div>

            {/* Рефералы по линиям */}
            {referralsStats && referralsStats.levelsStats && (
              <div className="referrals-by-level">
                <div className="stat-row stat-section-header">
                  <span className="stat-label">{t('profile.referralsByLevel')}</span>
                  <span className="stat-value">
                    {referralsStats.totalStats?.activeReferrals || 0}
                  </span>
                </div>
                {referralsStats.levelsStats.map((level) => (
                  <div key={level.level} className={`stat-row stat-level-row ${!level.isUnlocked ? 'locked' : 'unlocked'}`}>
                    <span className="stat-label">
                      <span className={`lock-icon ${level.isUnlocked ? 'unlocked' : 'locked'}`}>
                        {level.isUnlocked ? '✓' : '🔒'}
                      </span>
                      {t('profile.line')} {level.level}
                    </span>
                    <span className="stat-value">
                      {level.isUnlocked ? level.activeCount : `—`}
                    </span>
                  </div>
                ))}
                {referralsStats.totalStats?.availableLevel < 5 && (
                  <div className="referral-motivation">
                    <span>{t('profile.unlockNextLevel', { current: referralsStats.totalStats?.availableLevel || 1, next: (referralsStats.totalStats?.availableLevel || 1) + 1 })}</span>
                  </div>
                )}
              </div>
            )}

            {/* Fallback если нет данных рефералов по линиям */}
            {!referralsStats && (
              <div className="stat-row">
                <span className="stat-label">{t('profile.referralsInvited')}</span>
                <span className="stat-value">{profileStats?.referralsInvited || user?.referralCount || 0}</span>
              </div>
            )}
          </div>
        </div>

        {/* 📜 Публичная оферта — полный текст в документации */}
        <div className="stats-card">
          <h3 className="card-title">📜 {t('terms.title')}</h3>
          <p className="legal-hint">{t('terms.profileHint')}</p>
          <button
            className="legal-link-button"
            onClick={() => {
              const url = i18n.language === 'ru'
                ? 'https://gitbook.safepermoon.com/#oferta'
                : 'https://gitbook.safepermoon.com/en.html#oferta';
              const tg = (window as any).Telegram?.WebApp;
              if (tg?.openLink) tg.openLink(url);
              else window.open(url, '_blank');
            }}
          >
            {t('terms.read')}
          </button>
        </div>

        {/* TON Кошелек - убрано, так как уже есть в настройках в шапке */}
        {/* Настройки музыки - перенесены в меню настроек (шестеренка) */}
        {/* Настройки - убрано по запросу пользователя */}
      </div>
      
      {/* Модальное окно VIP статусов */}
      {showVIPModal && (
        <div className="modal-overlay" onClick={() => setShowVIPModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('profile.vipStatusesTitle')}</h2>
              <button className="modal-close" onClick={() => setShowVIPModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="info-section">
                <h3>{t('profile.vipStatusesSubtitle')}</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-icon">🥉</span>
                    <div className="info-details">
                      <strong>{t('profile.vip1')}</strong>
                      <p>{t('profile.vip1Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🥈</span>
                    <div className="info-details">
                      <strong>{t('profile.vip2')}</strong>
                      <p>{t('profile.vip2Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🥇</span>
                    <div className="info-details">
                      <strong>{t('profile.vip3')}</strong>
                      <p>{t('profile.vip3Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">💎</span>
                    <div className="info-details">
                      <strong>{t('profile.vip4')}</strong>
                      <p>{t('profile.vip4Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">💠</span>
                    <div className="info-details">
                      <strong>{t('profile.vip5')}</strong>
                      <p>{t('profile.vip5Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">👑</span>
                    <div className="info-details">
                      <strong>{t('profile.vip6')}</strong>
                      <p>{t('profile.vip6Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🔱</span>
                    <div className="info-details">
                      <strong>{t('profile.vip7')}</strong>
                      <p>{t('profile.vip7Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">⭐</span>
                    <div className="info-details">
                      <strong>{t('profile.vip8')}</strong>
                      <p>{t('profile.vip8Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🌟</span>
                    <div className="info-details">
                      <strong>{t('profile.vip9')}</strong>
                      <p>{t('profile.vip9Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">✨</span>
                    <div className="info-details">
                      <strong>{t('profile.vip10')}</strong>
                      <p>{t('profile.vip10Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>{t('profile.rewardStructureTitle')}</h3>
                <div className="rewards-grid">
                  <div className="reward-card">
                    <h4>{t('profile.oneTimePayment')}</h4>
                    <p>{t('profile.oneTimeDesc')}</p>
                    <div className="reward-percent">{t('profile.oneTimePercent')}</div>
                  </div>
                  <div className="reward-card">
                    <h4>{t('profile.dailyPercent')}</h4>
                    <p>{t('profile.dailyDesc')}</p>
                    <div className="reward-percent">{t('profile.dailyPercentValue')}</div>
                  </div>
                </div>
              </div>

              <div className="info-footer">
                <p>{t('profile.vipFooter')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно информации о бустах */}
      {showBoostsInfoModal && (
        <div className="modal-overlay" onClick={() => { SoundManager.playButtonClick(); setShowBoostsInfoModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('profile.boostsInfoTitle')}</h2>
              <button className="modal-close" onClick={() => { SoundManager.playButtonClick(); setShowBoostsInfoModal(false); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="info-section">
                <h3>{t('profile.boostsInfoSubtitle')}</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-icon">💎</span>
                    <div className="info-details">
                      <strong>{t('profile.boostType1')}</strong>
                      <p>{t('profile.boostType1Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🎖️</span>
                    <div className="info-details">
                      <strong>{t('profile.boostType2')}</strong>
                      <p>{t('profile.boostType2Desc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">👑</span>
                    <div className="info-details">
                      <strong>{t('profile.boostType3')}</strong>
                      <p>{t('profile.boostType3Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>{t('profile.boostsHowItWorks')}</h3>
                <p className="boosts-description">{t('profile.boostsHowItWorksDesc')}</p>
              </div>

              <div className="info-footer">
                <p>{t('profile.boostsFooter')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно информации о купонах */}
      {showCouponsInfoModal && (
        <div className="modal-overlay" onClick={() => { SoundManager.playButtonClick(); setShowCouponsInfoModal(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('profile.couponsInfoTitle')}</h2>
              <button className="modal-close" onClick={() => { SoundManager.playButtonClick(); setShowCouponsInfoModal(false); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="info-section">
                <h3>{t('profile.couponsInfoSubtitle')}</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-icon">🎁</span>
                    <div className="info-details">
                      <strong>{t('profile.couponsHow')}</strong>
                      <p>{t('profile.couponsHowDesc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">⚡</span>
                    <div className="info-details">
                      <strong>{t('profile.couponsWhat')}</strong>
                      <p>{t('profile.couponsWhatDesc')}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">🎮</span>
                    <div className="info-details">
                      <strong>{t('profile.couponsUse')}</strong>
                      <p>{t('profile.couponsUseDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-footer">
                <p>{t('profile.couponsFooter')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно активации купона */}
      {showCouponModal && activatedCoupon && (
        <div className="modal-overlay" onClick={() => setShowCouponModal(false)}>
          <div className="coupon-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="coupon-modal-icon">🎉</div>
            <h2 className="coupon-modal-title">{t('profile.couponActivated')}</h2>
            <div className="coupon-modal-info">
              <div className="coupon-modal-detail">
                <span className="coupon-modal-label">{t('profile.couponModalRank')}</span>
                <span className="coupon-modal-value">{t(`ranks.${activatedCoupon.rank_level}`)}</span>
              </div>
              <div className="coupon-modal-detail">
                <span className="coupon-modal-label">{t('profile.couponModalBoost')}</span>
                <span className="coupon-modal-value highlighted">+{activatedCoupon.boost_percentage}%</span>
              </div>
              <div className="coupon-modal-detail">
                <span className="coupon-modal-label">{t('profile.couponModalDuration')}</span>
                <span className="coupon-modal-value">{activatedCoupon.duration}</span>
              </div>
            </div>
            <p className="coupon-modal-description">
              {t('profile.couponModalDesc', { boost: activatedCoupon.boost_percentage, duration: activatedCoupon.duration })}
            </p>
            <button className="coupon-modal-close-btn" onClick={() => setShowCouponModal(false)}>
              {t('profile.couponModalClose')}
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно кастомизации луны - полноэкранное для мобильных */}
      {showCustomizationModal && catalogs && customization && (
        <div className="customization-fullscreen">
          <div className="customization-header">
            <button className="back-btn" onClick={() => { SoundManager.playButtonClick(); setShowCustomizationModal(false); }}>
              ← {t('common.back')}
            </button>
            <h2>🌙 {t('profile.customizeMoon')}</h2>
            <div style={{ width: '60px' }}></div>
          </div>

          <div className="customization-content">
            {/* Большое превью луны */}
            <div
              className="big-moon-preview"
              style={selectedBackground > 1 ? {
                backgroundImage: `url(${catalogs.backgrounds.find(b => b.id === selectedBackground)?.imagePath || ''})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              } : {}}
            >
              <div className={`moon-preview-container ${selectedEffects.map(id => {
                const eff = catalogs.effects.find(e => e.id === id);
                return eff ? `effect-${eff.code}` : '';
              }).join(' ')}`}>
                <img
                  src={catalogs.colors.find(c => c.id === selectedColor)?.imagePath || '/assets/moons/moon-1.png'}
                  alt="Moon"
                  className="big-moon-img"
                />
              </div>
            </div>

            {/* Выбор луны */}
            <div className="selection-section">
              <h4>{t('profile.selectColor')}</h4>
              <div className="moons-scroll">
                {catalogs.colors.map((color) => {
                  const isUnlocked = customization.unlockedColors.includes(color.id);
                  return (
                    <button
                      key={color.id}
                      className={`moon-item ${selectedColor === color.id ? 'selected' : ''} ${!isUnlocked ? 'locked' : ''}`}
                      onClick={() => isUnlocked && setSelectedColor(color.id)}
                      disabled={!isUnlocked}
                    >
                      <img src={color.imagePath} alt={color.name} />
                      {!isUnlocked && <div className="lock-overlay">🔒</div>}
                      {selectedColor === color.id && <div className="selected-check">✓</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Выбор фона */}
            <div className="selection-section">
              <h4>{t('profile.selectBackground')}</h4>
              <div className="backgrounds-scroll">
                {catalogs.backgrounds.map((bg) => {
                  const isUnlocked = customization.unlockedBackgrounds.includes(bg.id);
                  return (
                    <button
                      key={bg.id}
                      className={`bg-item ${selectedBackground === bg.id ? 'selected' : ''} ${!isUnlocked ? 'locked' : ''}`}
                      onClick={() => isUnlocked && setSelectedBackground(bg.id)}
                      disabled={!isUnlocked}
                      style={bg.id > 1 ? { backgroundImage: `url(${bg.imagePath})` } : {}}
                    >
                      {!isUnlocked && <div className="lock-overlay">🔒</div>}
                      {selectedBackground === bg.id && <div className="selected-check">✓</div>}
                      <span className="bg-label">{bg.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Выбор эффектов */}
            <div className="selection-section">
              <h4>{t('profile.selectEffects')}</h4>
              <div className="effects-row">
                {catalogs.effects.map((effect) => {
                  const isUnlocked = customization.unlockedEffects.includes(effect.id);
                  const isSelected = selectedEffects.includes(effect.id);
                  return (
                    <button
                      key={effect.id}
                      className={`effect-item ${isSelected ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}`}
                      onClick={() => isUnlocked && toggleEffect(effect.id)}
                      disabled={!isUnlocked}
                    >
                      <span className="effect-emoji">
                        {effect.code === 'glow' ? '✨' : effect.code === 'particles' ? '🌟' : '💫'}
                      </span>
                      <span className="effect-label">{effect.name}</span>
                      {!isUnlocked && <span className="lock-badge">🔒</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Кнопка сохранения */}
          <div className="customization-footer">
            <button
              className="save-btn"
              onClick={saveCustomization}
              disabled={savingCustomization}
            >
              {savingCustomization ? t('profile.saving') : t('profile.saveCustomization')}
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно настроек */}
      {/* Модальное окно настроек - убрано
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Настройки</h2>
              <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="settings-section">
                <h3>🎨 Качество графики</h3>
                <div className="settings-options">
                  <label className="settings-option">
                    <input 
                      type="radio" 
                      name="quality" 
                      value="low" 
                      checked={graphicsQuality === 'low'}
                      onChange={() => setGraphicsQuality('low')}
                    />
                    <span className="option-label">Низкое</span>
                  </label>
                  <label className="settings-option">
                    <input 
                      type="radio" 
                      name="quality" 
                      value="medium" 
                      checked={graphicsQuality === 'medium'}
                      onChange={() => setGraphicsQuality('medium')}
                    />
                    <span className="option-label">Среднее</span>
                  </label>
                  <label className="settings-option">
                    <input 
                      type="radio" 
                      name="quality" 
                      value="high" 
                      checked={graphicsQuality === 'high'}
                      onChange={() => setGraphicsQuality('high')}
                    />
                    <span className="option-label">Высокое</span>
                  </label>
                </div>
              </div>

              <div className="settings-section">
                <h3>🌍 Язык</h3>
                <div className="settings-options">
                  <label className="settings-option">
                    <input 
                      type="radio" 
                      name="language" 
                      value="ru" 
                      checked={language === 'ru'}
                      onChange={() => setLanguage('ru')}
                    />
                    <span className="option-label">🇷🇺 Русский</span>
                  </label>
                  <label className="settings-option">
                    <input 
                      type="radio" 
                      name="language" 
                      value="en" 
                      checked={language === 'en'}
                      onChange={() => setLanguage('en')}
                    />
                    <span className="option-label">🇬🇧 English</span>
                  </label>
                </div>
              </div>

              <div className="settings-section">
                <h3>🔊 Звуки</h3>
                <label className="settings-toggle">
                  <input 
                    type="checkbox" 
                    checked={soundEnabled}
                    onChange={(e) => setSoundEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">Звуковые эффекты</span>
                </label>
              </div>

              <div className="settings-footer">
                <button className="save-button" onClick={handleSaveSettings}>
                  💾 Сохранить настройки
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* PNL Modal */}
      <PnlModal isOpen={showPnlModal} onClose={() => setShowPnlModal(false)} />

      {/* Avatar Picker (web-mode) */}
      {showAvatarPicker && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={() => setShowAvatarPicker(false)}>
          <div style={{background:'#1a1a2e',borderRadius:'20px',padding:'24px',maxWidth:'360px',width:'100%',maxHeight:'80vh',overflow:'auto'}} onClick={e => e.stopPropagation()}>
            <h3 style={{textAlign:'center',marginBottom:'16px',fontSize:'18px',color:'#fff'}}>
              {t('profile.chooseAvatar') || 'Выберите аватар'}
            </h3>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:'12px'}}>
              {Array.from({length: 20}, (_, i) => {
                const num = String(i + 1).padStart(2, '0');
                const url = `/avatars/avatar_${num}.webp`;
                return (
                  <div key={i} onClick={() => {
                    localStorage.setItem('user_avatar', url);
                    setShowAvatarPicker(false);
                    window.location.reload();
                  }} style={{
                    cursor:'pointer',borderRadius:'50%',overflow:'hidden',
                    border: localStorage.getItem('user_avatar') === url ? '3px solid #f0b90b' : '3px solid transparent',
                    transition:'all 0.2s'
                  }}>
                    <img src={url} alt="" style={{width:'100%',height:'100%',display:'block'}} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Link Email Modal (из Telegram) */}
      {showLinkEmail && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={() => setShowLinkEmail(false)}>
          <div style={{background:'#1a1a2e',borderRadius:'20px',padding:'24px',maxWidth:'360px',width:'100%'}} onClick={e => e.stopPropagation()}>
            <h3 style={{textAlign:'center',marginBottom:'8px',fontSize:'18px',color:'#fff'}}>
              📧 {userEmail
                ? (t('profile.changeEmailTitle') || 'Сменить Email')
                : (t('profile.linkEmailTitle') || 'Привязать Email')
              }
            </h3>
            {userEmail && linkEmailStep === 'email' && (
              <p style={{textAlign:'center',fontSize:'12px',color:'rgba(255,255,255,0.35)',marginBottom:'8px'}}>
                {t('profile.currentEmail') || 'Текущий:'} {userEmail}
              </p>
            )}
            <p style={{textAlign:'center',fontSize:'13px',color:'#888',marginBottom:'20px',lineHeight:'1.5'}}>
              {linkEmailStep === 'email'
                ? (userEmail
                    ? (t('profile.changeEmailDesc') || 'Введите новый email — на него придёт код подтверждения')
                    : (t('profile.linkEmailDesc') || 'Введите email — на него придёт код подтверждения'))
                : (t('profile.linkEmailCodeDesc') || 'Введите 6-значный код из письма')
              }
            </p>
            {linkEmailError && (
              <p style={{textAlign:'center',fontSize:'13px',color:'#ff4444',marginBottom:'12px'}}>{linkEmailError}</p>
            )}
            {linkEmailStep === 'email' ? (
              <>
                <input
                  type="email"
                  value={linkEmailInput}
                  onChange={e => setLinkEmailInput(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width:'100%',padding:'14px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.15)',
                    borderRadius:'12px',color:'#fff',fontSize:'15px',marginBottom:'12px',boxSizing:'border-box',outline:'none'
                  }}
                />
                <button
                  disabled={linkEmailLoading || !linkEmailInput.includes('@')}
                  onClick={async () => {
                    setLinkEmailLoading(true);
                    setLinkEmailError('');
                    try {
                      const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
                      const resp = await fetch('/api/auth/email/send-code', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: linkEmailInput.toLowerCase().trim(), linkMode: true, telegramId: telegramId?.toString() })
                      });
                      const data = await resp.json();
                      if (data.success) {
                        setLinkEmailStep('code');
                      } else {
                        // Локализация ошибок
                        const errMap: Record<string, string> = {
                          'Email already linked to another account': t('profile.emailAlreadyLinked') || 'Этот email уже привязан к другому аккаунту',
                          'Email already linked to your account': t('profile.emailAlreadyYours') || 'Этот email уже привязан к вашему аккаунту',
                          'Too many requests. Try later.': t('profile.tooManyRequests') || 'Слишком много запросов. Попробуйте позже',
                          'Too many codes sent. Try in 1 hour.': t('profile.tooManyCodes') || 'Слишком много кодов. Подождите 1 час',
                          'Invalid email': t('profile.invalidEmail') || 'Неверный формат email',
                        };
                        setLinkEmailError(errMap[data.message] || data.message || 'Error');
                      }
                    } catch (e) {
                      setLinkEmailError(t('profile.networkError') || 'Ошибка сети');
                    }
                    setLinkEmailLoading(false);
                  }}
                  style={{
                    width:'100%',padding:'14px',background: linkEmailLoading ? '#555' : 'linear-gradient(180deg,#fcc830,#d49b00)',
                    border:'none',borderRadius:'12px',color:'#1a1200',fontWeight:600,fontSize:'15px',cursor:'pointer',opacity: !linkEmailInput.includes('@') ? 0.5 : 1
                  }}
                >
                  {linkEmailLoading ? '...' : (t('profile.sendCode') || 'Отправить код')}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={linkEmailCode}
                  onChange={e => setLinkEmailCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  style={{
                    width:'100%',padding:'14px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.15)',
                    borderRadius:'12px',color:'#fff',fontSize:'24px',fontWeight:700,letterSpacing:'8px',textAlign:'center',
                    marginBottom:'12px',boxSizing:'border-box',outline:'none',fontFamily:'monospace'
                  }}
                />
                <button
                  disabled={linkEmailLoading || linkEmailCode.length !== 6}
                  onClick={async () => {
                    setLinkEmailLoading(true);
                    setLinkEmailError('');
                    try {
                      const token = localStorage.getItem('token');
                      const resp = await fetch('/api/user/link-email', {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: linkEmailInput.toLowerCase().trim(), code: linkEmailCode })
                      });
                      const data = await resp.json();
                      if (data.success) {
                        setUserEmail(linkEmailInput.toLowerCase().trim());
                        setShowLinkEmail(false);
                        try { (window.Telegram?.WebApp as any)?.HapticFeedback?.notificationOccurred('success'); } catch(e) {}
                      } else {
                        const codeErrMap: Record<string, string> = {
                          'Wrong or expired code': t('profile.wrongCode') || 'Неверный или истёкший код',
                          'Email already linked to another account': t('profile.emailAlreadyLinked') || 'Этот email уже привязан к другому аккаунту',
                          'Email and code required': t('profile.codeRequired') || 'Введите email и код',
                        };
                        setLinkEmailError(codeErrMap[data.message] || data.message || t('profile.wrongCode') || 'Неверный код');
                      }
                    } catch (e) {
                      setLinkEmailError(t('profile.networkError') || 'Ошибка сети');
                    }
                    setLinkEmailLoading(false);
                  }}
                  style={{
                    width:'100%',padding:'14px',background: linkEmailLoading ? '#555' : 'linear-gradient(180deg,#fcc830,#d49b00)',
                    border:'none',borderRadius:'12px',color:'#1a1200',fontWeight:600,fontSize:'15px',cursor:'pointer',opacity: linkEmailCode.length !== 6 ? 0.5 : 1
                  }}
                >
                  {linkEmailLoading ? '...' : (t('profile.confirmCode') || 'Подтвердить')}
                </button>
                <p style={{textAlign:'center',fontSize:'12px',color:'#555',marginTop:'10px'}}>
                  {t('profile.linkEmailSentTo') || 'Код отправлен на'} {linkEmailInput}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Link Telegram Modal (web-mode) */}
      {showLinkTelegram && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={() => setShowLinkTelegram(false)}>
          <div style={{background:'#1a1a2e',borderRadius:'20px',padding:'24px',maxWidth:'360px',width:'100%'}} onClick={e => e.stopPropagation()}>
            <h3 style={{textAlign:'center',marginBottom:'8px',fontSize:'18px',color:'#fff'}}>
              {t('profile.linkTelegramTitle') || 'Привязать Telegram'}
            </h3>
            <p style={{textAlign:'center',fontSize:'13px',color:'#888',marginBottom:'20px',lineHeight:'1.5'}}>
              {t('profile.linkTelegramDesc') || 'Получите ссылку и откройте её в Telegram. Или в боте введите /email ваш@email.com'}
            </p>
            {!linkUrl ? (
              <button onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const resp = await fetch('/api/auth/email/generate-link', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                  });
                  const data = await resp.json();
                  if (data.success && data.botLink) setLinkUrl(data.botLink);
                  else if (data.alreadyLinked) { setShowLinkTelegram(false); alert('Telegram уже привязан!'); }
                } catch (e) { console.error(e); }
              }} style={{
                width:'100%',padding:'14px',background:'linear-gradient(180deg,#fcc830,#d49b00)',
                border:'none',borderRadius:'12px',color:'#1a1200',fontWeight:600,fontSize:'15px',cursor:'pointer'
              }}>
                {t('profile.getLinkBtn') || 'Получить ссылку'}
              </button>
            ) : (
              <div>
                <div style={{background:'rgba(0,136,204,0.1)',border:'1px solid rgba(0,136,204,0.3)',borderRadius:'12px',padding:'12px',marginBottom:'12px',wordBreak:'break-all',fontSize:'13px',color:'#0088cc',textAlign:'center'}}>
                  {linkUrl}
                </div>
                <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{
                  display:'block',width:'100%',padding:'14px',background:'#0088cc',
                  border:'none',borderRadius:'12px',color:'#fff',fontWeight:600,fontSize:'15px',
                  textAlign:'center',textDecoration:'none',marginBottom:'8px'
                }}>
                  {'Открыть в Telegram'}
                </a>
                <button onClick={(e) => {
                  navigator.clipboard.writeText(linkUrl);
                  const btn = e.currentTarget;
                  btn.textContent = '✅ Скопировано!';
                  btn.style.color = '#4caf50';
                  btn.style.borderColor = 'rgba(76,175,80,0.4)';
                  setTimeout(() => { btn.textContent = t('profile.copyLink') || 'Скопировать ссылку'; btn.style.color = '#888'; btn.style.borderColor = 'rgba(255,255,255,0.15)'; }, 2000);
                }} style={{
                  width:'100%',padding:'10px',background:'transparent',border:'1px solid rgba(255,255,255,0.15)',
                  borderRadius:'12px',color:'#888',fontSize:'13px',cursor:'pointer',transition:'color 0.2s, border-color 0.2s'
                }}>
                  {t('profile.copyLink') || 'Скопировать ссылку'}
                </button>
                <p style={{textAlign:'center',fontSize:'11px',color:'#555',marginTop:'12px'}}>
                  {t('profile.orManually') || 'Или в боте @moonraid_game_bot введите:'}<br/>
                  <code style={{color:'#f0b90b'}}>/email ваш@email.com</code>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;